import React, { useState, useEffect } from 'react';
import { LdsButton, LdsCard, LdsIcon, LdsLoadingSpinner, LdsModal, LdsToast, useToastContext } from '@elilillyco/ux-lds-react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authconfig';
import './Admin.scss';
import data from "../data/env.config.json";
import getCurrentEnv from '../data/utils/getCurrentEnv';
import infoToastConfig from '../components/shared/Toast';

// File Table Component for RAG Knowledge Base
const EMBEDDING_STATUS_MAP = {
  embedded: { className: 'status-embedded', label: '📚 Embedded' },
  finished: { className: 'status-finished', label: '✅ Finished' },
  running: { className: 'status-running', label: '🔄 Processing' },
  queued: { className: 'status-queued', label: '⏳ Queued' },
  failed: { className: 'status-failed', label: '❌ Failed' },
};

const getEmbeddingForFile = (fileName, jobs) => {
  if (!fileName || !Array.isArray(jobs)) return null;
  const target = fileName.toLowerCase();
  // Newest matching job wins (jobs list may contain multiple entries per file).
  for (let i = jobs.length - 1; i >= 0; i--) {
    const job = jobs[i];
    let jobFile = '';
    if (job.docs && job.docs.length > 0 && job.docs[0].length > 1) {
      const fd = job.docs[0][1];
      jobFile = (typeof fd === 'string' ? fd : String(fd)).split('/').pop();
    }
    if (jobFile && jobFile.toLowerCase() === target) {
      return job;
    }
  }
  return null;
};

const FileTable = ({ files, loading, onDelete, onReEmbed, reEmbedding, embeddingJobs = [] }) => {
  if (loading) {
    return (
      <div className="text-center p-5">
        <LdsLoadingSpinner />
      </div>
    );
  }

  if (!files || files.length === 0) {
    return (
      <div className="text-center p-5">
        <p>No files have been uploaded to the knowledge base yet.</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th className="px-4">File Name</th>
            <th className="px-4">Type</th>
            <th className="px-4">Size</th>
            <th className="px-4">Uploaded Date</th>
            <th className="px-4">Embedding Status</th>
            <th className="px-4 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const job = getEmbeddingForFile(file.fileName, embeddingJobs);
            const statusKey = (job && job.status) ? job.status.toLowerCase() : 'pending';
            const mapped = EMBEDDING_STATUS_MAP[statusKey] || { className: 'status-pending', label: '⏳ Pending' };
            const canReEmbed = statusKey === 'pending' || statusKey === 'failed';
            const isReEmbedding = reEmbedding === file.fileName;
            return (
            <tr key={file.id}>
              <td className="px-4">{file.fileName}</td>
              <td className="px-4">{file.fileType}</td>
              <td className="px-4">{file.fileSize}</td>
              <td className="px-4">{new Date(file.uploadedDate).toLocaleString()}</td>
              <td className="px-4">
                <span className={`status-badge ${mapped.className}`} title={job && job.error_message ? job.error_message : ''}>
                  {mapped.label}
                </span>
              </td>
              <td className="px-4 text-center">
                <div className="d-flex justify-content-center">
                  {canReEmbed && (
                    <LdsButton
                      variant="tertiary"
                      onClick={() => onReEmbed(file.fileName)}
                      ariaLabel={`Re-embed ${file.fileName}`}
                      title="Re-embed to Cortex"
                      className="action-button reembed-action-button"
                      disabled={isReEmbedding}
                    >
                      {isReEmbedding ? (
                        <LdsLoadingSpinner size="sm" />
                      ) : (
                        <LdsIcon name="ArrowsClockwiseFill" label="Re-embed" inline />
                      )}
                    </LdsButton>
                  )}
                  {file.downloadUrl && (
                    <a
                      href={file.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LdsButton
                        variant="tertiary"
                        ariaLabel={`Download ${file.fileName}`}
                        className="action-button"
                      >
                        <LdsIcon name="DownloadSimple" label={`Download ${file.fileName}`} inline />
                      </LdsButton>
                    </a>
                  )}
                  <LdsButton
                    variant="tertiary-error"
                    onClick={() => onDelete(file.fileName)}
                    ariaLabel={`Delete ${file.fileName}`}
                    className="action-button"
                  >
                    <LdsIcon name="TrashSimpleFill" label={`Delete ${file.fileName}`} inline />
                  </LdsButton>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// File Upload Modal Component
const MAX_FILE_SIZE_MB = 4.5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const FileUploadModal = ({ isModalOpen, setIsModalOpen, existingFiles = [], onUploadSuccess, onUploadError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileSizeError, setFileSizeError] = useState('');
  const [timer] = useState(180);
  
  const { instance: modalInstance, accounts: modalAccounts } = useMsal();

  const getTokenFromMsal = async () => {
    if (!modalAccounts || modalAccounts.length === 0) {
      throw new Error('No authenticated account found. Please log in again.');
    }
    try {
      const resp = await modalInstance.acquireTokenSilent({
        ...loginRequest,
        account: modalAccounts[0],
      });
      return resp.accessToken;
    } catch (error) {
      console.error('[FileUploadModal] Error acquiring token from MSAL:', error);
      throw new Error('Failed to acquire authentication token. Please try again.');
    }
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileSizeError(`File too large: ${(file.size / (1024 * 1024)).toFixed(2)} MB. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`);
      setSelectedFile(null);
    } else if (existingFiles.some(f => f.fileName === file.name)) {
      setFileSizeError(`"${file.name}" already exists in the knowledge base. Delete it first before re-uploading.`);
      setSelectedFile(null);
    } else {
      setFileSizeError('');
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (event) => {
    validateAndSetFile(event.target.files[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    validateAndSetFile(event.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const fileName = selectedFile.name;

    try {
      const token = await getTokenFromMsal();
      const apiUrl = data[getCurrentEnv()].api_url;

      // Step 1: Upload file through Lambda (avoids S3 CORS issues on deployed site)
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const uploadResponse = await fetch(
        `${apiUrl}/cortex-api-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            request_type: 'upload_file',
            file_name: fileName,
            file_content: fileBase64
          })
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('S3 upload failed. Please try again.');
      }

      // Step 2: Trigger Cortex embedding (S3 file → Cortex vector store)
      const embedResponse = await fetch(
        `${apiUrl}/cortex-api-data?request_type=embed_to_cortex&folder_name=&file_name=${encodeURIComponent(fileName)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!embedResponse.ok) {
        // Rollback: remove the S3 file since embedding failed
        await fetch(`${apiUrl}/cortex-api-data?request_type=delete_s3_file&file_name=${encodeURIComponent(fileName)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {});
        throw new Error('Embedding failed. The uploaded file has been removed. Please try again.');
      }

      // Both steps succeeded — close modal and trigger parent callback (which shows toast + refreshes)
      setIsModalOpen(false);
      setSelectedFile(null);
      setFileSizeError('');
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setIsModalOpen(false);
      setSelectedFile(null);
      setFileSizeError('');
      if (onUploadError) {
        onUploadError(error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <LdsModal
      modalId="upload-modal"
      open={isModalOpen}
      setModalOpen={setIsModalOpen}
      heading="Upload Document to Knowledge Base"
      size="lg"
    >
      <div className="p-4">
        <div
          className={`drop-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('fileInput').click()}
        >
          <p style={{ fontSize: '24px', marginBottom: '10px' }}>☁️⬆️</p>
          <p>Drag and drop a file here, or click to browse</p>
          <p className="text-muted">Supported formats: PDF, DOCX, TXT, MD</p>
          <input
            id="fileInput"
            type="file"
            accept=".pdf,.docx,.txt,.md"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {fileSizeError && (
          <div className="mt-3" style={{ color: '#d32f2f', fontSize: '14px' }}>
            ⚠️ {fileSizeError}
          </div>
        )}

        {selectedFile && (
          <div className="mt-3">
            <p><strong>Selected file:</strong> {selectedFile.name}</p>
            <p><strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB</p>
            <p className="text-muted">Upload expires in: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}</p>
          </div>
        )}

        <div className="mt-4 d-flex justify-content-end">
          <LdsButton
            variant="secondary"
            onClick={() => { setIsModalOpen(false); setFileSizeError(''); setSelectedFile(null); }}
            className="me-2"
          >
            Cancel
          </LdsButton>
          <LdsButton
            onClick={handleUpload}
            disabled={!selectedFile || uploading || !!fileSizeError}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </LdsButton>
        </div>
      </div>
    </LdsModal>
  );
};

// Main Admin Component
const Admin = () => {
  // Get MSAL instance for secure token acquisition
  const { instance, accounts } = useMsal();
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [embeddingJobs, setEmbeddingJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [embeddedFilesCount, setEmbeddedFilesCount] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reEmbedding, setReEmbedding] = useState(null); // fileName being re-embedded
  const [uploadSuccessFlag, setUploadSuccessFlag] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);
  const { addToast } = useToastContext();

  // Helper function to get token from MSAL securely
  const getTokenFromMsal = async () => {
    if (!accounts || accounts.length === 0) {
      throw new Error('No authenticated account found. Please log in again.');
    }
    try {
      const resp = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return resp.accessToken;
    } catch (error) {
      console.error('[Admin] Error acquiring token from MSAL:', error);
      throw new Error('Failed to acquire authentication token. Please try again.');
    }
  };

  // Show upload toast after modal-close render cycle completes
  useEffect(() => {
    if (uploadSuccessFlag) {
      addToast(infoToastConfig(
        'File uploaded successfully! Please wait a few minutes for it to be embedded into the AI knowledge base.',
        'success',
        8000
      ));
      setUploadSuccessFlag(false);
    }
  }, [uploadSuccessFlag, addToast]);

  useEffect(() => {
    if (uploadErrorMessage) {
      addToast(infoToastConfig(uploadErrorMessage, 'error', 6000));
      setUploadErrorMessage(null);
    }
  }, [uploadErrorMessage, addToast]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchFiles();
    fetchEmbeddingJobs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh jobs every 15 seconds if any are queued/running
  useEffect(() => {
    const hasActiveJobs = embeddingJobs.some(
      job => job.status === 'queued' || job.status === 'running'
    );
    if (hasActiveJobs) {
      const interval = setInterval(() => {
        fetchEmbeddingJobs();
      }, 15000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddingJobs]);

  const fetchFiles = async () => {
    try {
      const token = await getTokenFromMsal();
      const apiUrl = data[getCurrentEnv()].api_url;
      
      // folder_path empty because S3_FOLDER_PREFIX already contains 'specialty_hub/cortex_docs/'
      const response = await fetch(
        `${apiUrl}/cortex-api-data?request_type=list_files&folder_path=`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFiles(data.data.files || []);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      addToast(infoToastConfig('Failed to load files', 'error', 3000));
    } finally {
      setLoading(false);
    }
  };

  const fetchEmbeddingJobs = async () => {
    setJobsLoading(true);
    try {
      const token = await getTokenFromMsal();
      const apiUrl = data[getCurrentEnv()].api_url;
      
      // Fetch embedding job status
      const jobsResponse = await fetch(
        `${apiUrl}/cortex-api-data?request_type=embed_status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (jobsResponse.ok) {
        const responseData = await jobsResponse.json();
        const jobs = Array.isArray(responseData.data) ? responseData.data : [responseData.data];
        setEmbeddingJobs(jobs.filter(j => j && j.job_id));
      }

      // Fetch list of embedded files from Cortex vector store
      const embeddedResponse = await fetch(
        `${apiUrl}/cortex-api-data?request_type=list_embedded_files`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (embeddedResponse.ok) {
        const embeddedData = await embeddedResponse.json();
        
        // Cortex /data/list-files returns: { data: { files: [{ file: "name.pdf", size: 12345 }] } }
        if (embeddedData.data && Array.isArray(embeddedData.data.files)) {
          // Update embedded files count for metrics
          setEmbeddedFilesCount(embeddedData.data.files.length);
          
          const embeddedFilesAsJobs = embeddedData.data.files.map(fileObj => ({
            job_id: `embedded-${fileObj.file}`,
            status: 'embedded',
            docs: [[null, fileObj.file]], // fileObj.file is the filename string
            error_message: null,
            file_size: fileObj.size
          }));
          setEmbeddingJobs(prevJobs => [...prevJobs, ...embeddedFilesAsJobs]);
        }
      }
    } catch (error) {
      console.error('Error fetching embedding jobs:', error);
    } finally {
      setJobsLoading(false);
    }
  };

  const handleDelete = async (fileName) => {
    setFileToDelete(fileName);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    const fileName = fileToDelete;
    setDeleting(true);

    try {
      const token = await getTokenFromMsal();
      const apiUrl = data[getCurrentEnv()].api_url;

      const response = await fetch(
        `${apiUrl}/cortex-api-data?request_type=delete_file&file_name=${encodeURIComponent(fileName)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const responseData = await response.json();

      if (response.ok && responseData.statusCode === 200) {
        addToast(infoToastConfig(
          `"${fileName}" deleted successfully from S3 and knowledge base`,
          'success',
          5000
        ));
        handleRefresh();
      } else {
        throw new Error(responseData.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      addToast(infoToastConfig(`Failed to delete file: ${error.message}`, 'error', 5000));
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setFileToDelete(null);
    }
  };

  const handleReEmbed = async (fileName) => {
    setReEmbedding(fileName);
    try {
      const token = sessionStorage.getItem('Token');
      const apiUrl = data[getCurrentEnv()].api_url;

      const response = await fetch(
        `${apiUrl}/cortex-api-data?request_type=embed_to_cortex&folder_name=&file_name=${encodeURIComponent(fileName)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        addToast(infoToastConfig(
          `"${fileName}" submitted for embedding. Please wait a few minutes for processing.`,
          'success',
          6000
        ));
        fetchEmbeddingJobs();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Embedding request failed');
      }
    } catch (error) {
      console.error('Re-embed error:', error);
      addToast(infoToastConfig(`Failed to re-embed file: ${error.message}`, 'error', 5000));
    } finally {
      setReEmbedding(null);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchFiles();
    fetchEmbeddingJobs();
  };

  return (
    <div className="admin-container p-4">
      <h1>Specialty Hub Administration</h1>
      
      <LdsCard className="mt-4">
        <div className="p-4">
          <h2>RAG Knowledge Base</h2>
          <p className="text-muted mb-4">
            Manage the files that power the Cortex RAG chatbot's knowledge base.
          </p>
          
          <div className="d-flex justify-content-end mb-4">
            <LdsButton
              onClick={handleRefresh}
              variant="secondary"
              className="me-2"
              disabled={jobsLoading}
            >
              {jobsLoading ? 'Refreshing...' : '🔄 Refresh'}
            </LdsButton>
            <LdsButton
              onClick={() => setIsUploadModalOpen(true)}
            >
              ☁️ Upload File
            </LdsButton>
          </div>

          {embeddingJobs.some(j => j.status === 'queued' || j.status === 'running') && (
            <div className="mb-3" style={{
              padding: '10px 15px',
              backgroundColor: '#fff3cd',
              borderRadius: '6px',
              border: '1px solid #ffc107',
              fontSize: '14px'
            }}>
              ⏳ Some documents are still being processed. Status auto-refreshes every 15 seconds.
            </div>
          )}

          <FileTable
            files={files}
            loading={loading}
            onDelete={handleDelete}
            onReEmbed={handleReEmbed}
            reEmbedding={reEmbedding}
            embeddingJobs={embeddingJobs}
          />
        </div>
      </LdsCard>

      <LdsCard className="mt-4">
        <div className="p-4">
          <h3>RAG System Performance</h3>
          <div className="d-flex justify-content-around mt-4">
            <div className="text-center">
              <h4 className="mb-2">Embedded Files</h4>
              <p className="display-4">{embeddedFilesCount}</p>
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                Files in vector store
              </p>
            </div>
            <div className="text-center">
              <h4 className="mb-2">System Availability</h4>
              <p className="display-4">100%</p>
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                Cortex API status
              </p>
            </div>
          </div>
        </div>
      </LdsCard>

      <FileUploadModal
        isModalOpen={isUploadModalOpen}
        setIsModalOpen={setIsUploadModalOpen}
        existingFiles={files}
        onUploadSuccess={() => {
          handleRefresh();
          setUploadSuccessFlag(true);
        }}
        onUploadError={(msg) => {
          setUploadErrorMessage(msg);
        }}
      />

      <LdsModal
        modalId="delete-confirm-modal"
        open={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        heading="Delete Document"
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '12px' }}>
            Are you sure you want to delete <strong>"{fileToDelete}"</strong>?
          </p>
          <p style={{ marginBottom: '12px', color: '#d32f2f' }}>
            This action will permanently:
          </p>
          <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
            <li>Remove the file from <strong>S3 storage</strong></li>
            <li>Delete all embeddings from the <strong>Cortex data config</strong> (vector store)</li>
            <li>Make this document unavailable for the <strong>AI chatbot</strong> to reference</li>
          </ul>
          <p style={{ color: '#666', fontSize: '14px' }}>
            This action cannot be undone.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
          <LdsButton
            variant="secondary"
            onClick={() => { setDeleteModalOpen(false); setFileToDelete(null); }}
            disabled={deleting}
          >
            Cancel
          </LdsButton>
          <LdsButton
            variant="primary"
            onClick={confirmDelete}
            disabled={deleting}
            style={{ backgroundColor: '#d32f2f', borderColor: '#d32f2f' }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </LdsButton>
        </div>
      </LdsModal>
      <LdsToast />
    </div>
  );
};

export default Admin;
